import type {
  AnnouncementCreatePayload,
  AnnouncementRow,
  AnnouncementUpdatePayload,
} from "@/modules/announcements/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type { MarketingPushMonitorResponse } from "@/shared/notifications/marketing-push-monitor.types";

const base = `${config.apiUrl}/announcements`;

export type ListAnnouncementsParams = {
  isActive?: "true" | "false";
  priority?: "NORMAL" | "HIGH" | "URGENT";
  search?: string;
  limit?: number;
  offset?: number;
  sortField?: "title" | "publishAt" | "priority";
  sortOrder?: "asc" | "desc";
};

export type ListAnnouncementsResult = {
  data: AnnouncementRow[];
  total: number;
};

export const announcementsApi = {
  list: (params?: ListAnnouncementsParams): Promise<ListAnnouncementsResult> => {
    const search = new URLSearchParams();
    if (params?.isActive) search.set("isActive", params.isActive);
    if (params?.priority) search.set("priority", params.priority);
    if (params?.search) search.set("search", params.search);
    if (params?.limit != null) search.set("limit", String(params.limit));
    if (params?.offset != null) search.set("offset", String(params.offset));
    if (params?.sortField) search.set("sortField", params.sortField);
    if (params?.sortOrder) search.set("sortOrder", params.sortOrder);
    const q = search.toString();
    return fetcher.get(`${base}${q ? `?${q}` : ""}`);
  },

  get: (id: string): Promise<AnnouncementRow> =>
    fetcher.get<AnnouncementRow>(`${base}/${id}`),

  create: (payload: AnnouncementCreatePayload): Promise<AnnouncementRow> =>
    fetcher.post<AnnouncementRow>(base, payload),

  update: (
    id: string,
    payload: AnnouncementUpdatePayload,
  ): Promise<AnnouncementRow> =>
    fetcher.put<AnnouncementRow>(`${base}/${id}`, payload),

  toggleActive: (id: string, isActive: boolean): Promise<AnnouncementRow> =>
    fetcher.patch<AnnouncementRow>(`${base}/${id}/active`, { isActive }),

  delete: (id: string): Promise<{ ok: true }> =>
    fetcher.delete<{ ok: true }>(`${base}/${id}`),

  resendPush: (
    id: string,
  ): Promise<{ ok: true; enqueuedAt: string; announcement: AnnouncementRow }> =>
    fetcher.post(`${base}/${id}/resend-push`, {}),

  pushMonitor: (id: string): Promise<MarketingPushMonitorResponse> =>
    fetcher.get(`${base}/${id}/push-monitor`),
};
